import { Alert, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  withdrawalsApi,
  type CounterpartyHistory,
  type ExpectedActivity,
} from '../api/withdrawals.api.js';
import { formatTransactionAmount as money } from '../utils/transaction-money.js';
import { APP_LOCALE } from '../utils/locale.js';

/**
 * What this customer has done with this beneficiary before.
 *
 * Screening tells an officer whether the party is on a list. It does not tell
 * them whether the payment is normal for this customer, which is the question
 * that catches invoice fraud and layering. Everything here is arithmetic over
 * payments we have already seen — nothing new is fetched or stored.
 *
 * It states facts and does not score them. "First payment to this account" is
 * ordinary for a customer paying a new supplier and is also what the first leg
 * of a diversion looks like; which one it is, is the officer's call.
 */
function usable(value: CounterpartyHistory | undefined): CounterpartyHistory | null {
  if (value === undefined || value === null || Array.isArray(value)) return null;
  const ok =
    typeof value.priorPayments === 'number' &&
    typeof value.settledPayments === 'number' &&
    typeof value.refusedPayments === 'number' &&
    typeof value.otherCustomers === 'number' &&
    typeof value.firstTime === 'boolean' &&
    typeof value.unusuallyLarge === 'boolean' &&
    typeof value.settledCents === 'string' &&
    typeof value.currency === 'string';
  return ok ? value : null;
}

/**
 * What the customer declared at onboarding, against what they are doing now.
 *
 * "Activity consistent with the customer profile" is a test we are required to
 * apply, and the declaration behind it was collected at onboarding and then
 * shown to nobody. Exceeding your own estimate is common and innocent, and it
 * is also the first observable sign of an account being used for something
 * other than what it was opened for -- so this reports the comparison and
 * leaves the judgement where it belongs.
 */
function ExpectedActivityLines({ data }: { data: ExpectedActivity }): JSX.Element | null {
  if (data.noDeclaration)
    return (
      <Alert severity="info">
        This customer declared no expected volumes at onboarding, so there is nothing to compare
        this payment against.
      </Alert>
    );
  return (
    <Stack spacing={1}>
      {data.overDeclaredMax && data.declaredMaxSingle !== null && (
        <Alert severity="warning">
          <strong>Larger than the customer said they would send.</strong> At onboarding they gave
          a maximum single transaction of {data.declaredMaxSingle}.
        </Alert>
      )}
      {data.maxSingleUnreadable && data.declaredMaxSingle !== null && (
        <Alert severity="info">
          The declared maximum single transaction reads &ldquo;{data.declaredMaxSingle}&rdquo;,
          which is not a figure this can compare against. Check it by eye.
        </Alert>
      )}
      {(data.declaredOutgoingVolume !== null || data.declaredOutgoingCount !== null) && (
        <Typography variant="body2" color="text.secondary">
          Declared outgoing activity:{' '}
          {data.declaredOutgoingVolume ?? 'volume not given'}
          {data.declaredOutgoingCount !== null && `, ${data.declaredOutgoingCount}`}.
        </Typography>
      )}
    </Stack>
  );
}

export function CounterpartyHistoryPanel({ withdrawalId }: { withdrawalId: string }): JSX.Element | null {
  const query = useQuery({
    queryKey: ['counterparty-history', withdrawalId],
    queryFn: () => withdrawalsApi.counterparty(withdrawalId),
    retry: false,
  });
  const activity = useQuery({
    queryKey: ['expected-activity', withdrawalId],
    queryFn: () => withdrawalsApi.expectedActivity(withdrawalId),
    retry: false,
  });

  if (query.isPending)
    return <Typography variant="body2">Checking what we know about this beneficiary…</Typography>;
  // A missing history must not read as a clean one.
  if (query.isError)
    return (
      <Alert severity="warning">
        The beneficiary history could not be loaded, so this payment has not been checked against
        what this customer has sent before.
      </Alert>
    );

  // A panel that trusts the shape of what came back is how money(undefined)
  // threw and blanked the entire review console once already. An unexpected
  // payload is treated as "not loaded", never as "nothing to report".
  const data = usable(query.data);
  if (data === null)
    return (
      <Alert severity="warning">
        The beneficiary history could not be read, so this payment has not been checked against
        what this customer has sent before.
      </Alert>
    );

  return (
    <Stack spacing={1} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2">This beneficiary</Typography>

      {data.firstTime ? (
        <Alert severity="warning">
          <strong>First payment to this account.</strong> This customer has not sent here before.
        </Alert>
      ) : (
        <Typography variant="body2">
          {data.priorPayments} previous payment{data.priorPayments === 1 ? '' : 's'} to this
          account, {data.settledPayments} of which settled, totalling{' '}
          {money(data.settledCents, data.currency)}.
          {data.lastPaidAt !== null && ` Last one ${new Date(data.lastPaidAt).toLocaleDateString(APP_LOCALE)}.`}
        </Typography>
      )}

      {data.refusedPayments > 0 && (
        <Alert severity="warning">
          {data.refusedPayments} earlier payment{data.refusedPayments === 1 ? '' : 's'} to this
          account {data.refusedPayments === 1 ? 'was' : 'were'} refused, returned or failed.
        </Alert>
      )}

      {data.unusuallyLarge && data.largestPriorCents !== null && (
        <Alert severity="warning">
          Larger than anything this customer has sent to this account before — the previous
          largest was {money(data.largestPriorCents, data.currency)}.
        </Alert>
      )}

      {data.otherCustomers > 0 && (
        <Alert severity="info">
          {data.otherCustomers} other customer{data.otherCustomers === 1 ? '' : 's'} of ours
          {data.otherCustomers === 1 ? ' has' : ' have'} also paid this account. Ordinary between
          related businesses; worth a look otherwise.
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ pt: 1 }}>
        Against what they told us
      </Typography>
      {activity.isError ? (
        <Alert severity="warning">
          What this customer declared at onboarding could not be read, so this payment has not
          been checked against it.
        </Alert>
      ) : activity.isPending ? (
        <Typography variant="body2">Checking what they declared…</Typography>
      ) : (
        <ExpectedActivityLines data={activity.data} />
      )}
    </Stack>
  );
}
